import { useEffect, useState } from "react";
import { Highlighter, Magnet, MousePointer2, Scissors, Square, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Kbd } from "@/components/ui/kbd";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Slider } from "@/components/ui/slider";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import type { DraftAction } from "../../masterImages/draft";
import type { BrickRegion, BrickType } from "../../masterImages/types";
import BrickTypeToggle from "../BrickSeries/BrickTypeToggle";
import { HALF_TINT, REGION_COLOURS, isBrush, type Tool } from "./EditorCanvas";
import { CONTOUR, CUT_AWAY } from "./useOutlines";

interface Props {
  tool: Tool;
  onTool(tool: Tool): void;
  brushRadius: number;
  onBrushRadius(radius: number): void;
  tolerance: number;
  selected: BrickRegion[];
  dispatch(action: DraftAction): void;
}

const Swatch: React.FC<{ type: BrickType }> = ({ type }) => (
  <span
    className="inline-block h-3.5 w-5.5 shrink-0"
    style={{
      border: `2px ${type === "half" ? "dashed" : "solid"} ${REGION_COLOURS[type]}`,
      background: type === "half" ? HALF_TINT : "transparent",
    }}
  />
);

const TOOLS: { value: Tool; icon: React.ElementType; label: string; title: string }[] = [
  {
    value: "select",
    icon: MousePointer2,
    label: "Selecteren",
    title: "Regio's selecteren, verplaatsen en schalen",
  },
  { value: "draw", icon: Square, label: "Tekenen", title: "Een nieuwe regio tekenen" },
  {
    value: "brushAdd",
    icon: Highlighter,
    label: "Erbij",
    title: "Kwast: steen aan de omtrek toevoegen",
  },
  {
    value: "brushErase",
    icon: Scissors,
    label: "Eraf",
    title: "Kwast: voeg uit de omtrek knippen",
  },
  {
    value: "brushSnap",
    icon: Magnet,
    label: "Rand",
    title: "Kwast: de omtrek naar de rand van de steen trekken",
  },
];

const BRUSH_HINTS = {
  brushAdd:
    "Schilder over delen die bij de steen horen. De omtrek past zich aan terwijl je schildert.",
  brushErase: "Schilder over voeg die weg moet. De omtrek past zich aan terwijl je schildert.",
  brushSnap:
    "Schilder langs een steenrand: onder de kwast wordt voeg weggeknipt en steen behouden, zodat de omtrek de rand volgt.",
};

const SHORTCUTS: { keys: React.ReactNode; action: string }[] = [
  {
    keys: (
      <>
        <Kbd>←</Kbd>
        <Kbd>→</Kbd>
        <Kbd>↑</Kbd>
        <Kbd>↓</Kbd>
      </>
    ),
    action: "1 px verschuiven",
  },
  {
    keys: (
      <>
        <Kbd>Shift</Kbd>+ pijltjes
      </>
    ),
    action: "10 px verschuiven",
  },
  { keys: <Kbd>F</Kbd>, action: "Heel of half wisselen" },
  { keys: <Kbd>Delete</Kbd>, action: "Selectie verwijderen" },
  { keys: <Kbd>Esc</Kbd>, action: "Selectie opheffen" },
  {
    keys: (
      <>
        <Kbd>⌘</Kbd>/<Kbd>Ctrl</Kbd>+<Kbd>Z</Kbd>
      </>
    ),
    action: "Ongedaan maken",
  },
  {
    keys: (
      <>
        <Kbd>⌘</Kbd>/<Kbd>Ctrl</Kbd>+<Kbd>Shift</Kbd>+<Kbd>Z</Kbd>
      </>
    ),
    action: "Opnieuw",
  },
  {
    keys: (
      <>
        <Kbd>Shift</Kbd>+ klik
      </>
    ),
    action: "Aan selectie toevoegen",
  },
  {
    keys: (
      <>
        <Kbd>Spatie</Kbd>+ slepen
      </>
    ),
    action: "Pannen (of sleep op een lege plek)",
  },
  { keys: "Scrollen", action: "Zoomen" },
];

const Toolbar: React.FC<Props> = ({
  tool,
  onTool,
  brushRadius,
  onBrushRadius,
  tolerance,
  selected,
  dispatch,
}) => {
  const [globalTolerance, setGlobalTolerance] = useState(tolerance);
  useEffect(() => setGlobalTolerance(tolerance), [tolerance]);
  const override = selected.length > 0 && selected.every((r) => r.tolerance != null);
  const [regionTolerance, setRegionTolerance] = useState(tolerance);
  useEffect(() => setRegionTolerance(selected[0]?.tolerance ?? tolerance), [selected, tolerance]);
  const selectedType = selected.every((r) => r.type === selected[0]?.type)
    ? selected[0]?.type
    : undefined;

  return (
    <section className="grid gap-4">
      <ToggleGroup
        type="single"
        rovingFocus={false}
        value={tool}
        onValueChange={(value) => value && onTool(value as Tool)}
        className="grid w-full grid-cols-5"
        aria-label="Gereedschap"
      >
        {TOOLS.map(({ value, icon: Icon, label, title }) => (
          <ToggleGroupItem
            key={value}
            value={value}
            title={title}
            aria-label={title}
            className="h-auto flex-col gap-1 px-1 py-1.5 text-xs"
          >
            <Icon />
            {label}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>

      <div className="grid gap-1.5 text-sm">
        <div className="flex gap-4">
          <span className="flex items-center gap-1.5">
            <Swatch type="full" />
            Hele steen
          </span>
          <span className="flex items-center gap-1.5">
            <Swatch type="half" />
            Halve steen
          </span>
        </div>
        <span className="flex items-start gap-1.5 text-muted-foreground">
          <span
            className="mt-0.5 inline-block h-3.5 w-5.5 shrink-0"
            style={{ background: CUT_AWAY, outline: `1px solid ${CONTOUR}`, outlineOffset: -4 }}
          />
          Gedimd is weggeknipt, de witte lijn is de omtrek van de steen.
        </span>
      </div>

      {isBrush(tool) && (
        <div className="grid gap-2 rounded-lg bg-muted/60 p-3">
          <p className="text-sm text-muted-foreground">{BRUSH_HINTS[tool]}</p>
          <div className="flex items-center justify-between text-sm font-medium">
            Kwastgrootte <span className="text-muted-foreground tabular-nums">{brushRadius}</span>
          </div>
          <Slider
            aria-label="Kwastgrootte"
            min={2}
            max={60}
            value={[brushRadius]}
            onValueChange={([value]) => onBrushRadius(value)}
          />
        </div>
      )}

      <div className="grid gap-2">
        <div className="flex items-center justify-between text-sm font-semibold">
          Voegtolerantie
          <span className="font-normal text-muted-foreground tabular-nums">{globalTolerance}</span>
        </div>
        <Slider
          aria-label="Voegtolerantie"
          min={0}
          max={100}
          value={[globalTolerance]}
          onValueChange={([value]) => setGlobalTolerance(value)}
          onValueCommit={([value]) => dispatch({ type: "setTolerance", tolerance: value })}
        />
      </div>

      {selected.length > 0 && (
        <div className="grid gap-3 rounded-lg border p-3">
          <h2 className="text-sm font-semibold">
            {selected.length} {selected.length === 1 ? "regio" : "regio's"} geselecteerd
          </h2>
          <div className="flex items-center gap-2">
            <BrickTypeToggle
              className="flex-1"
              value={selectedType}
              onChange={(brickType) => dispatch({ type: "setType", brickType })}
            />
            <Button
              variant="outline"
              size="sm"
              className="text-destructive hover:text-destructive"
              onClick={() => dispatch({ type: "delete" })}
            >
              <Trash2 />
              Verwijderen
            </Button>
          </div>
          {selectedType === undefined && (
            <p className="text-sm text-muted-foreground">De selectie bevat hele en halve stenen.</p>
          )}
          {selected.some((r) => r.strokes.length > 0) && (
            <Button variant="outline" size="sm" onClick={() => dispatch({ type: "clearStrokes" })}>
              Kwaststreken wissen
            </Button>
          )}
          <div className="flex items-center gap-2">
            <Checkbox
              id="own-tolerance"
              checked={override}
              onCheckedChange={(checked) =>
                dispatch({
                  type: "setRegionTolerance",
                  tolerance: checked === true ? regionTolerance : null,
                })
              }
            />
            <Label htmlFor="own-tolerance" className="font-normal">
              Eigen tolerantie
            </Label>
            {override && (
              <span className="ml-auto text-sm text-muted-foreground tabular-nums">
                {regionTolerance}
              </span>
            )}
          </div>
          {override && (
            <Slider
              aria-label="Eigen tolerantie"
              min={0}
              max={100}
              value={[regionTolerance]}
              onValueChange={([value]) => setRegionTolerance(value)}
              onValueCommit={([value]) =>
                dispatch({ type: "setRegionTolerance", tolerance: value })
              }
            />
          )}
        </div>
      )}

      <Separator />
      <div className="grid gap-2">
        <h2 className="text-sm font-semibold">Sneltoetsen</h2>
        <ul className="grid gap-1.5 text-sm">
          {SHORTCUTS.map(({ keys, action }) => (
            <li key={action} className="flex items-center justify-between gap-2">
              <span className="text-muted-foreground">{action}</span>
              <span className="flex items-center gap-0.5 whitespace-nowrap text-xs">{keys}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

export default Toolbar;
